import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Printer, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { printBadge, silentPrintStatus } from "@/lib/visitorBadge";
import { fmtHM } from "@/lib/format";

export default function KioskSuccess() {
  const { state } = useLocation();
  const nav = useNavigate();
  const [badgeCfg, setBadgeCfg] = useState(null);
  const autoPrinted = useRef(false);
  const [printed, setPrinted] = useState(false);
  const isIn = state?.mode === "in";
  const canBadge = isIn && !!state?.visit && !!badgeCfg?.enabled;

  useEffect(() => {
    const t = setTimeout(() => nav("/kiosk"), canBadge ? 20000 : 8000);
    return () => clearTimeout(t);
  }, [nav, canBadge]);

  useEffect(() => {
    if (!isIn || !state?.visit) return;
    api.get("/settings/badge").then((r) => setBadgeCfg(r.data)).catch(() => {});
  }, [isIn, state]);

  useEffect(() => {
    if (canBadge && badgeCfg.auto_print && !autoPrinted.current) {
      autoPrinted.current = true;
      printBadge(state.visit, badgeCfg).then(({ silent }) => setPrinted(!!silent));
    }
  }, [canBadge, badgeCfg, state]);

  if (!state) return <Link to="/kiosk" className="m-12 inline-block">Return →</Link>;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 safe-area" data-testid="kiosk-success">
      <div className="kiosk-rise w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-12 md:p-16 text-center shadow-sm">
        <div className="mx-auto inline-flex h-24 w-24 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mb-8">
          <CheckCircle2 className="h-14 w-14" strokeWidth={2.25} />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-emerald-700 mb-2">
          {isIn ? "Welcome" : "Goodbye"}
        </div>
        <h1 className="font-display text-5xl font-bold tracking-tight text-slate-900">
          {isIn ? `Hello, ${state.name}` : `Thanks, ${state.name}`}
        </h1>
        <p className="mt-4 text-xl text-slate-600">
          {isIn
            ? <>You're checked in to visit <span className="font-semibold text-slate-900">{state.host}</span>. Please proceed to reception.</>
            : <>You're checked out{state.duration ? ` after ${fmtHM(state.duration)}` : ""}. Have a great day!</>}
        </p>

        {printed && (
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-800" data-testid="success-badge-printed">
            <BadgeCheck className="h-4 w-4" /> Your badge is printing — please take it from the printer.
          </div>
        )}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {canBadge && (
            <button
              type="button"
              onClick={() => printBadge(state.visit, badgeCfg)}
              data-testid="success-print-badge-btn"
              className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-primary bg-white px-8 py-4 text-primary font-display text-lg font-semibold tracking-tight transition active:scale-[0.98] hover:bg-primary/5"
            >
              <Printer className="h-5 w-5" /> {badgeCfg.auto_print && (printed || silentPrintStatus() === "1") ? "Print badge again" : "Print badge"}
            </button>
          )}
          <Link
            to="/kiosk"
            data-testid="success-done-btn"
            className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-10 py-4 text-white font-display text-lg font-semibold tracking-tight transition active:scale-[0.98] hover:bg-slate-800"
          >
            Done
          </Link>
        </div>
        <div className="mt-6 text-xs uppercase tracking-widest text-slate-400">
          Returning to the welcome screen…
        </div>
      </div>
    </div>
  );
}
